/* Wizard chrome — sticky top-of-page navigator for the Pitch Editor's
   14-step flow (Brand as step 1 → 11 remaining core slides → Funder-ready
   check → Generate). Renders the step-pill row, sub-slide chips, hover
   previews, progress bar, readiness caption, keyboard shortcuts, and the
   ⌘K / Ctrl+K jump palette.
   Extracted from `PitchEditor.jsx` (~270 lines) to keep the editor page
   under 2.5k lines and give the wizard chrome its own home.

   Iter56 (Feb 2026):
    • "Brand" pill relabelled to "01" so the whole nav reads as a numbered
      sequence (01 → 12, Check, Ship).
    • Go-to-Market (slide 7) removed from the wizard flow entirely — the
      underlying slide.num stays 7 in the DB so existing decks aren't
      corrupted, but it's hidden from the nav, sidebar, and PPTX export.
      All step→slide-num translations go through `STEP_TO_SLIDE` below. */

import React, { useEffect, useState } from "react";
import { SUB_SLIDE_ARROW_SIZE } from "@/components/WizardSlidePreviewAndNav";

// Brand tokens — kept local to this component so it stays self-contained.
const C = {
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

/** Ordered mapping of wizard-step index → underlying slide.num.
 *  Step 1 = Brand (no slide), Steps 2-12 = core slides in visible order
 *  (slide 7 GTM is skipped), Step 13 = Check, Step 14 = Ship.
 *  Slide.num stays the DB source of truth — this table is the sole
 *  place we translate between "presentational step" and "storage num".
 *  Keeping it as a single named array means the mapping is auditable
 *  in one line if we ever change the slide order again. */
export const STEP_TO_SLIDE = [
  null, // step 1 = Brand
  1, 2, 3, 4, 5, 6,        // steps 2-7 → slides 1-6 (Cover…Traction)
  8, 9, 10, 11, 12,        // steps 8-12 → slides 8-12 (GTM=7 skipped)
  null, // step 13 = Check
  null, // step 14 = Ship
];
export const TOTAL_STEPS = STEP_TO_SLIDE.length;   // 14
export const STEP_CHECK = TOTAL_STEPS - 1;         // 13
export const STEP_SHIP = TOTAL_STEPS;              // 14
export const NUMBERED_STEPS = STEP_CHECK - 1;      // 12
export const CORE_SLIDES_COUNT = NUMBERED_STEPS - 1; // 11 (excludes Brand)

/** Inverse lookup: slide.num → wizard step index. Returns null if the
 *  slide is intentionally hidden (GTM). */
export function slideNumToStep(slideNum) {
  const idx = STEP_TO_SLIDE.indexOf(slideNum);
  return idx === -1 ? null : idx + 1;
}

export default function WizardChrome({
  deck,
  view,
  activeSlide,
  activeExtraId,
  onGoto,
  onGotoBrand,
  onGotoCheck,
  onGotoGenerate,
}) {
  const totalSteps = TOTAL_STEPS;
  // Map current view/slide → external step number (1-14).
  const currentStep = (() => {
    if (view === "brand") return 1;
    if (view === "check") return STEP_CHECK;
    if (view === "generate") return STEP_SHIP;
    // view === "slides" — find the visible step matching this slide.num.
    // GTM (slide 7) has no step; fall back to Business Model's step so
    // legacy deep-links don't stall.
    const step = slideNumToStep(activeSlide) || slideNumToStep(6) || 2;
    return step;
  })();
  const pct = Math.round((currentStep / totalSteps) * 100);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const filledSlides = (deck.slides || []).filter(
    (s) => s.num !== 7 // GTM excluded from count (iter56)
      && ((s.content || "").trim().length > 20
        || (s.num === 9 && (deck.team || []).length > 0)
        || (s.num === 10 && ((deck.projections || {}).years || []).length > 0)
        || (s.num === 12 && ((deck.closing || {}).email || (deck.closing || {}).website)))
  ).length;

  // For each external step, resolve the human-readable title.
  const stepLabel = currentStep === 1
    ? "Brand & style"
    : currentStep === STEP_CHECK
      ? "Funder-ready check"
      : currentStep === STEP_SHIP
        ? "Generate & download"
        : (deck.slides.find((s) => s.num === STEP_TO_SLIDE[currentStep - 1])?.title || "Slide");

  const isFilled = (stepNum) => {
    if (stepNum === 1) {
      return !!(deck.brand_color && deck.brand_color !== "#0A8B85") || !!deck.tagline || !!deck.logo;
    }
    const slideNum = STEP_TO_SLIDE[stepNum - 1];
    if (slideNum == null) return false;
    const s = (deck.slides || []).find((x) => x.num === slideNum);
    return !!s && ((s.content || "").trim().length > 20
      || (slideNum === 9 && (deck.team || []).length > 0)
      || (slideNum === 10 && ((deck.projections || {}).years || []).length > 0)
      || (slideNum === 12 && ((deck.closing || {}).email || (deck.closing || {}).website)));
  };

  const gotoStep = (n) => {
    if (n === 1 && onGotoBrand) onGotoBrand();
    else if (n === STEP_CHECK && onGotoCheck) onGotoCheck();
    else if (n === STEP_SHIP && onGotoGenerate) onGotoGenerate();
    else if (n >= 2 && n < STEP_CHECK && onGoto) {
      const slideNum = STEP_TO_SLIDE[n - 1];
      if (slideNum != null) onGoto(slideNum);
    }
  };

  // Small helper: how many extras (sub-slides) does the slide for this
  // wizard step have? Used to render the "↳ N" sub-slide chip below the
  // parent pill so founders can jump directly to a sub-slide without
  // hunting for it in a menu.
  const extrasForStep = (n) => {
    const slideNum = STEP_TO_SLIDE[n - 1];
    if (slideNum == null) return [];
    const s = (deck.slides || []).find((x) => x.num === slideNum);
    return (s && s.extras) || [];
  };

  // Keyboard shortcuts — arrows move between steps, Cmd/Ctrl+K opens the palette.
  // Skip when the user is typing so we don't hijack normal input keys.
  useEffect(() => {
    const isTyping = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (paletteOpen) return; // let the palette handle its own keys
      if (isTyping(e.target)) return;
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === "ArrowRight" && currentStep < totalSteps) {
        e.preventDefault();
        gotoStep(currentStep + 1);
      } else if (e.key === "ArrowLeft" && currentStep > 1) {
        e.preventDefault();
        gotoStep(currentStep - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, paletteOpen]);

  const stepMeta = (n) => {
    if (n === 1) return { title: "Brand & style", subtitle: "Logo, colour, font, tagline", content: "" };
    if (n === STEP_CHECK) return { title: "Funder-ready check", subtitle: "AI critique against 3 funder pillars", content: "" };
    if (n === STEP_SHIP) return { title: "Generate & download", subtitle: "Ship the .pptx to funders", content: "" };
    const slideNum = STEP_TO_SLIDE[n - 1];
    const s = (deck.slides || []).find((x) => x.num === slideNum) || {};
    return {
      title: s.title || `Slide ${slideNum}`,
      subtitle: s.question || "",
      content: (s.content || "").trim(),
    };
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 pt-6" data-testid="wizard-chrome">
      {/* Step pill row — quick-jump navigator with hover previews.
          Iter62: pills now distribute evenly across the container using
          a 14-column CSS grid (12 numbered + Check + Ship). Removes the
          horizontal scrollbar that was creeping in and eliminates the
          vertical scroll indicator caused by `overflow-y-visible`. Grid
          also gives each pill an equal share of width so 01…12 don't
          bunch to the left with empty space on the right. On very narrow
          viewports (<640px) we fall back to a horizontal flex row with a
          subtle scroll — but at >=640px it's always fully distributed. */}
      <div className="mb-3 grid gap-1.5 items-start"
        data-testid="wizard-step-nav"
        style={{ gridTemplateColumns: `repeat(${totalSteps}, minmax(0, 1fr))` }}>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => {
          const active = n === currentStep;
          const filled = isFilled(n);
          const label = n === STEP_CHECK
            ? "Check"
            : n === STEP_SHIP
              ? "Ship"
              : String(n).padStart(2, "0");
          const meta = stepMeta(n);
          return (
            <div key={n} className="relative group">
              <button onClick={() => gotoStep(n)}
                data-testid={`wizard-step-pill-${n}`}
                className="w-full text-[10.5px] font-bold rounded-md transition-all inline-flex items-center justify-center gap-1"
                style={{
                  padding: "6px 8px",
                  backgroundColor: active
                    ? C.teal
                    : (filled ? "rgba(10, 139, 133, 0.16)" : "rgba(255, 255, 255, 0.04)"),
                  color: active
                    ? "#fff"
                    : (filled ? C.tealSoft : "rgba(255, 255, 255, 0.50)"),
                  border: `1px solid ${active ? C.teal : (filled ? "rgba(10, 139, 133, 0.4)" : "rgba(255, 255, 255, 0.08)")}`,
                }}>
                {filled && !active && (
                  <span className="inline-block rounded-full shrink-0"
                    style={{ width: 4, height: 4, backgroundColor: C.tealSoft }} />
                )}
                <span className="truncate">{label}</span>
              </button>
              {/* Sub-slide chip — appears below the parent step pill when
                  the slide has one or more extras. Clicking jumps straight
                  to the first extra so founders can find spilled content
                  even after the "Open sub-slide" toast dismisses. */}
              {(() => {
                const extras = extrasForStep(n);
                if (extras.length === 0) return null;
                const anyActive = active && activeExtraId;
                const slideNum = STEP_TO_SLIDE[n - 1];
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onGoto && slideNum != null) onGoto(slideNum, extras[0].id);
                    }}
                    data-testid={`wizard-step-substep-${n}`}
                    title={`Sub-slide${extras.length > 1 ? 's' : ''}: ${extras.map((x) => x.title).join(', ')}`}
                    className="mt-1 mx-auto flex items-center justify-center gap-1 text-[9px] font-bold rounded transition-all whitespace-nowrap"
                    style={{
                      padding: "2px 6px",
                      backgroundColor: anyActive ? "rgba(245, 158, 11, 0.28)" : "rgba(245, 158, 11, 0.12)",
                      color: C.orange,
                      border: `1px solid ${anyActive ? "rgba(245, 158, 11, 0.6)" : "rgba(245, 158, 11, 0.32)"}`,
                    }}
                  >
                    <span style={{ fontSize: SUB_SLIDE_ARROW_SIZE, marginRight: 2 }}>↳</span>
                    {extras.length === 1 ? "Sub-slide" : `${extras.length} sub-slides`}
                  </button>
                );
              })()}
              {/* Hover preview — mini table-of-contents card */}
              <div className="absolute left-1/2 top-full mt-2 z-30 -translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`wizard-step-preview-${n}`}
                style={{ width: 240 }}>
                <div className="rounded-lg p-3 shadow-2xl"
                  style={{ backgroundColor: "#050E0D", border: "1px solid rgba(123, 211, 206, 0.22)" }}>
                  <div className="text-[9px] font-bold uppercase tracking-[0.20em] mb-1"
                    style={{ color: filled ? C.tealSoft : "rgba(255,255,255,0.35)" }}>
                    Step {n} · {filled ? "Filled" : (active ? "Current" : "Empty")}
                  </div>
                  <div className="font-extrabold text-[13px] text-white tracking-tight leading-tight" style={{ letterSpacing: "-0.01em" }}>
                    {meta.title}
                  </div>
                  {meta.subtitle && (
                    <div className="text-[10.5px] text-white/50 italic mt-1 leading-snug">
                      {meta.subtitle}
                    </div>
                  )}
                  {meta.content && (
                    <div className="mt-2 pt-2 border-t space-y-0.5"
                      style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                      {meta.content.split("\n")
                        .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
                        .filter(Boolean)
                        .slice(0, 3)
                        .map((line, i) => (
                          <div key={i} className="text-[10.5px] text-white/70 leading-snug truncate">
                            <span className="mr-1" style={{ color: deck.brand_color || C.teal }}>·</span>
                            {line}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>
          Step <span className="text-white font-mono-num">{currentStep}</span> of {totalSteps} · {stepLabel}
        </div>
        <div className="text-[11px] text-white/55 font-mono-num" data-testid="wizard-progress-label">
          {pct}% complete
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
        <div className="h-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${C.teal} 0%, ${C.orange} 100%)`,
          }}
          data-testid="wizard-progress-bar" />
      </div>
      <div className="mt-1 text-[10.5px] text-white/45 flex items-center gap-3 flex-wrap">
        <span>{filledSlides} of {CORE_SLIDES_COUNT} core slides filled · tap any step above to jump.</span>
        <span className="hidden sm:inline text-white/35">
          Shortcuts: <kbd className="px-1 py-0.5 rounded font-mono-num text-[10px]" style={{ backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}>←</kbd>
          {" "}<kbd className="px-1 py-0.5 rounded font-mono-num text-[10px]" style={{ backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}>→</kbd> move ·{" "}
          <kbd className="px-1 py-0.5 rounded font-mono-num text-[10px]" style={{ backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}>⌘K</kbd> jump
        </span>
      </div>

      {paletteOpen && (
        <StepJumpPalette
          onClose={() => setPaletteOpen(false)}
          onGoto={(n) => { setPaletteOpen(false); gotoStep(n); }}
          currentStep={currentStep}
          totalSteps={totalSteps}
          stepMeta={stepMeta}
          isFilled={isFilled}
        />
      )}
    </div>
  );
}

/* Command-palette style overlay opened by ⌘K / Ctrl+K. Type-ahead filter + arrow
   navigation to jump to any step without touching the mouse. */
function StepJumpPalette({ onClose, onGoto, currentStep, totalSteps, stepMeta, isFilled }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);

  const all = Array.from({ length: totalSteps }, (_, i) => i + 1);
  const filtered = q.trim() === ""
    ? all
    : all.filter((n) => {
        const m = stepMeta(n);
        return (`${n} ${m.title} ${m.subtitle}`).toLowerCase().includes(q.trim().toLowerCase());
      });

  useEffect(() => {
    setIdx(0);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(filtered.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); return; }
      if (e.key === "Enter") {
        e.preventDefault();
        if (filtered[idx]) onGoto(filtered[idx]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, idx, onGoto, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="wizard-step-palette">
      <div className="w-full max-w-md rounded-xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: "#0A2826", border: "1px solid rgba(123, 211, 206, 0.22)" }}
        onClick={(e) => e.stopPropagation()}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Jump to a step… (type a number or a keyword)"
          data-testid="wizard-step-palette-input"
          className="w-full px-4 py-3.5 text-sm bg-transparent outline-none text-white"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }} />
        <div className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-white/45">No steps match &ldquo;{q}&rdquo;</div>
          )}
          {filtered.map((n, i) => {
            const meta = stepMeta(n);
            const active = i === idx;
            const isCurrent = n === currentStep;
            const filled = isFilled(n);
            return (
              <button key={n} onClick={() => onGoto(n)}
                onMouseEnter={() => setIdx(i)}
                data-testid={`wizard-step-palette-item-${n}`}
                className="w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors"
                style={{ backgroundColor: active ? "rgba(10, 139, 133, 0.15)" : "transparent" }}>
                <span className="text-[10px] font-bold font-mono-num shrink-0 w-8"
                  style={{ color: filled ? C.tealSoft : "rgba(255,255,255,0.35)" }}>
                  {n <= NUMBERED_STEPS ? String(n).padStart(2, "0") : (n === STEP_CHECK ? "CK" : "SH")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-white truncate">
                    {meta.title}
                    {isCurrent && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest font-bold"
                        style={{ color: C.orange }}>Current</span>
                    )}
                  </div>
                  {meta.subtitle && (
                    <div className="text-[10.5px] text-white/45 truncate italic">{meta.subtitle}</div>
                  )}
                </div>
                {filled && !isCurrent && (
                  <span className="text-[9px] uppercase tracking-widest font-bold shrink-0"
                    style={{ color: C.tealSoft }}>Filled</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 text-[10px] text-white/40 flex items-center gap-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <span>
            <kbd className="px-1 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>↑</kbd>
            {" "}<kbd className="px-1 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>↓</kbd> navigate
          </span>
          <span><kbd className="px-1 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>↵</kbd> jump</span>
          <span><kbd className="px-1 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
